import { useState, useRef } from "react";
import ClassNames from "Helpers/Common";
import { Badge, PaginationFooter, EmptyRow, TableSkeleton } from "../components";
import { formatMins, timeAgo } from "../format";

// Debounced search input (300ms) with a clear button. onSearch fires the trimmed
// value; the caller drives a server query off it (and resets to page 1).
export const SearchBar = ({ placeholder = "Search…", onSearch }) => {
  const [text, setText] = useState("");
  const timer = useRef();
  const change = (v) => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(v.trim()), 300);
  };
  return (
    <div className="relative w-64">
      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
      </span>
      <input
        value={text}
        onChange={(e) => change(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-md pl-9 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-chill"
      />
      {text && (
        <button
          type="button"
          onClick={() => change("")}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          ×
        </button>
      )}
    </div>
  );
};

/* A server-paginated, server-sorted table shared by the Patients and Doctors
   tabs. `columns` = [{ key, label, sortable, align, render }]. Sort/paging state
   lives in the dashboard so the query key drives refetch. */
export const UserTable = ({ title, subtitle, columns, data, sort, onSort, onPage, loading, action, emptyText }) => {
  const rows = data?.rows || [];
  const perPage = data?.perPage || 25;
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / perPage));

  const SortHeader = ({ col }) => {
    const active = sort?.key === col.key;
    const arrow = active ? (sort.order === "asc" ? "▲" : "▼") : "";
    return (
      <th
        className={ClassNames(
          "px-6 py-4 text-xs font-normal text-gray-400 align-bottom whitespace-nowrap",
          col.align === "right" ? "text-right" : "text-left"
        )}
      >
        {col.sortable ? (
          <button
            type="button"
            onClick={() => onSort(col.key)}
            className={ClassNames(
              "focus:outline-none hover:text-gray-600",
              active && "text-blue-chill font-medium"
            )}
          >
            {col.label} {arrow}
          </button>
        ) : (
          col.label
        )}
      </th>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <span className="text-lg font-medium sapphire">{title}</span>
          {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
        </div>
        {action}
      </div>
      {loading && rows.length === 0 ? (
        <TableSkeleton rows={6} cols={columns.length} />
      ) : (
        <div className="rounded-lg bg-white shadow-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr>
                {columns.map((c) => (
                  <SortHeader key={c.key} col={c} />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <EmptyRow colSpan={columns.length} text={emptyText || "No users with activity in this period."} />
              )}
              {rows.map((row) => (
                <tr key={row.patientId || row.doctorId} className="hover:bg-gray-50">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={ClassNames(
                        "px-6 py-3 text-sm whitespace-nowrap",
                        c.align === "right" ? "text-right" : "text-left"
                      )}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationFooter
            page={data?.page || 1}
            totalPages={totalPages}
            total={data?.total || 0}
            onPage={onPage}
          />
        </div>
      )}
    </div>
  );
};

export const shortDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
export const money = (n) => (n || n === 0 ? `$${Number(n).toFixed(2)}` : "—");

export const nameCell = (primary, secondary) => (
  <div className="flex flex-col min-w-0">
    <span className="font-medium sapphire truncate">{primary}</span>
    {secondary && <span className="text-xs text-gray-400 truncate">{secondary}</span>}
  </div>
);

export const PATIENT_COLUMNS = [
  { key: "name", label: "Patient", render: (r) => nameCell(r.name, r.state) },
  {
    key: "status",
    label: "Status",
    render: (r) =>
      r.status === "Pending" ? (
        <span className="text-gray-400">No visit</span>
      ) : (
        <Badge tone="green">{r.status}</Badge>
      ),
  },
  { key: "signedUp", label: "Signed up", sortable: true, render: (r) => <span className="text-gray-500">{shortDate(r.signedUp)}</span> },
  {
    key: "activation",
    label: "Activation",
    render: (r) => <span className="text-gray-500">{r.activationLagMins != null ? formatMins(r.activationLagMins) : "—"}</span>,
  },
  { key: "visits", label: "Visits", render: (r) => r.visits },
  { key: "revenue", label: "Revenue", render: (r) => <span className="font-medium sapphire">{money(r.revenue)}</span> },
  { key: "rx", label: "Rx", render: (r) => <span className="text-gray-500">{r.rxCount}</span> },
  { key: "lastVisit", label: "Last seen", render: (r) => <span className="text-gray-500">{timeAgo(r.lastVisit)}</span> },
];

export const PROVIDER_COLUMNS = [
  { key: "name", label: "Provider", render: (r) => nameCell(r.name, r.specialty) },
  { key: "signedUp", label: "Onboarded", sortable: true, render: (r) => <span className="text-gray-500">{shortDate(r.signedUp)}</span> },
  {
    key: "avgRating",
    label: "Rating",
    render: (r) =>
      r.avgRating != null ? (
        <span className={r.avgRating < 3 ? "text-red-600" : "sapphire"}>
          {r.avgRating.toFixed(1)}★ <span className="text-xs text-gray-400">({r.ratingCount})</span>
        </span>
      ) : (
        <span className="text-gray-400">no ratings</span>
      ),
  },
  {
    key: "online",
    label: "Online",
    render: (r) =>
      r.online === null ? (
        <span className="text-gray-400">—</span>
      ) : r.online ? (
        <Badge tone="green">Online</Badge>
      ) : (
        <span className="text-gray-400">offline</span>
      ),
  },
  { key: "coverage", label: "States", render: (r) => <span className="text-gray-500">{r.coverage}</span> },
  { key: "visitsHandled", label: "Visits", render: (r) => r.visitsHandled },
  {
    key: "completionRate",
    label: "Completion",
    render: (r) =>
      r.completionRate != null ? (
        <span className={r.completionRate < 90 ? "text-red-600" : "text-gray-500"}>{r.completionRate}%</span>
      ) : (
        "—"
      ),
  },
  { key: "avgAcceptMins", label: "Avg accept", render: (r) => <span className="text-gray-500">{r.avgAcceptMins != null ? formatMins(r.avgAcceptMins) : "—"}</span> },
  { key: "avgDurationMins", label: "Avg visit", render: (r) => <span className="text-gray-500">{r.avgDurationMins != null ? formatMins(r.avgDurationMins) : "—"}</span> },
  { key: "rxWritten", label: "Rx", render: (r) => <span className="text-gray-500">{r.rxWritten}</span> },
  { key: "lastActive", label: "Last active", render: (r) => <span className="text-gray-500">{timeAgo(r.lastActive)}</span> },
];
